import { useApiAction } from "api/drops/useApiAction";
import {
  agentPauseTelephoneApi,
  pauseTypesGetTelephoneApi,
  userGetTelephoneApi,
  userLogOnOffTelephoneApi,
} from "api/puzzel/puzzel";
import { useFeatureFlag } from "app/FeatureFlags/useFeatureFlags";
import { PUZZEL_OIDC_LOGIN_FLAG } from "app/PuzzelAuth/puzzelAuthConfig";
import {
  PauseType,
  PresenceStatus,
} from "features/Header/PuzzelTelephone/components/types";
import { usePuzzelAuthenticationContext } from "features/Header/PuzzelTelephone/provider/contexts/PuzzelAuthenticationContext";
import { useUserValidationState } from "features/Header/PuzzelTelephone/provider/contexts/UserValidationContext";
import React, { FC, ReactNode, useEffect, useState } from "react";

// Maps the CC5 "LoggedOff" | "LoggedOn" | "Pause" result to our own status - exported so
// Logon.tsx can apply the same mapping to the live-polled extended state information.
export const mapResultToPresenceStatus = (result: string): PresenceStatus => {
  if (result === "LoggedOff") return "loggedOff";
  if (result === "Pause") return "paused";
  return "available";
};

type UserDetails = {
  currentProfile: any;
  profiles: any;
  isLoggedOn: boolean | null;
  presenceStatus: PresenceStatus | null;
  isPresencePending: boolean;
  lastActionAt: number;
  pauseTypes: PauseType[];
  hasRealPauseTypes: boolean;
};

type CallStatusValue = {
  userDetails: UserDetails;
  performUserLogOnOff: (args: any) => void;
  performAgentPauseOn: (pauseTypeId?: number) => void;
  performAgentPauseOff: () => void;
};

// Single shared context - CallControl/CallOut/ListenForCallStatusContext etc. all read from
// this regardless of which body (legacy/OIDC) is currently mounted below.
const CallStatusContext = React.createContext<CallStatusValue | undefined>(
  undefined,
);

// --- Legacy: plain logon/logoff, no Ledig/Pause presence feature ---------------------------
// TODO: remove this body (and the puzzelOidcLogin flag/branch) once OIDC has rolled out fully.

function useLegacyCallStatus(): CallStatusValue {
  const { userId } = useUserValidationState();

  const { isAuthenticated } = usePuzzelAuthenticationContext();

  const [userGetRequest, performUserGet]: any = useApiAction(
    userGetTelephoneApi,
    false,
  );

  const [userLogOnOffRequest, performUserLogOnOff] = useApiAction(
    userLogOnOffTelephoneApi,
    false,
  );

  useEffect(() => {
    if (isAuthenticated && userId) {
      performUserGet({ userId });
    }
  }, [isAuthenticated, userLogOnOffRequest.data, userId]);

  const profileId = userGetRequest.data?.result?.currentProfileId;
  const currentProfile = profileId
    ? userGetRequest.data?.result?.profiles.find(
        (profile: { id: number }) => profile.id === profileId,
      )
    : null;
  const profiles = userGetRequest.data?.result?.profiles;

  const statusLogonFromLogOnOffRequest = userLogOnOffRequest.data?.result
    ? userLogOnOffRequest.data?.result !== "LoggedOff"
    : undefined;
  const statusLoginFromUserDetailsRequest = userGetRequest.data?.result
    ?.contactCentreStatus
    ? userGetRequest.data?.result?.contactCentreStatus !== "LoggedOff"
    : undefined;

  let isLoggedOn: boolean | null = null;
  if (statusLogonFromLogOnOffRequest !== undefined) {
    isLoggedOn = statusLogonFromLogOnOffRequest;
  } else if (statusLoginFromUserDetailsRequest !== undefined) {
    isLoggedOn = statusLoginFromUserDetailsRequest;
  }

  return {
    userDetails: {
      currentProfile,
      profiles,
      isLoggedOn,
      presenceStatus: null,
      isPresencePending: false,
      lastActionAt: 0,
      pauseTypes: [],
      hasRealPauseTypes: false,
    },
    performUserLogOnOff,
    performAgentPauseOn: () => {},
    performAgentPauseOff: () => {},
  };
}

// --- OIDC: logon/logoff + Ledig/Pause/Logg av presence dropdown -----------------------------

function useOidcCallStatus(): CallStatusValue {
  const { userId } = useUserValidationState();

  const { isAuthenticated } = usePuzzelAuthenticationContext();

  const [userGetRequest, performUserGet]: any = useApiAction(
    userGetTelephoneApi,
    false,
  );

  const [
    userLogOnOffRequest,
    performUserLogOnOffRaw,
    resetUserLogOnOffRequest,
  ] = useApiAction(userLogOnOffTelephoneApi, false);

  const [pauseRequest, performAgentPauseRaw, resetPauseRequest] = useApiAction(
    agentPauseTelephoneApi,
    false,
    false,
  );

  const [pauseTypesRequest, performPauseTypesGet]: any = useApiAction(
    pauseTypesGetTelephoneApi,
    false,
  );

  useEffect(() => {
    if (isAuthenticated && userId) {
      performUserGet({ userId });
    }
  }, [isAuthenticated, userLogOnOffRequest.data, pauseRequest.data, userId]);

  const profileId = userGetRequest.data?.result?.currentProfileId;
  const currentProfile = profileId
    ? userGetRequest.data?.result?.profiles.find(
        (profile: { id: number }) => profile.id === profileId,
      )
    : null;
  const profiles = userGetRequest.data?.result?.profiles;

  // Whichever action ran most recently wins (the other is reset to null when one is performed).
  const statusFromPauseRequest = pauseRequest.data?.result
    ? mapResultToPresenceStatus(pauseRequest.data.result)
    : undefined;
  const statusFromLogOnOffRequest = userLogOnOffRequest.data?.result
    ? mapResultToPresenceStatus(userLogOnOffRequest.data.result)
    : undefined;
  const statusFromUserDetailsRequest = userGetRequest.data?.result
    ?.contactCentreStatus
    ? mapResultToPresenceStatus(userGetRequest.data.result.contactCentreStatus)
    : undefined;

  let presenceStatus: PresenceStatus | null = null;
  if (statusFromPauseRequest !== undefined) {
    presenceStatus = statusFromPauseRequest;
  } else if (statusFromLogOnOffRequest !== undefined) {
    presenceStatus = statusFromLogOnOffRequest;
  } else if (statusFromUserDetailsRequest !== undefined) {
    presenceStatus = statusFromUserDetailsRequest;
  }

  const isLoggedOn =
    presenceStatus === null ? null : presenceStatus !== "loggedOff";

  // True while a pause/unpause request is in flight - disables the status dropdown (Logon.tsx)
  // so it can't be clicked again mid-request.
  const isPresencePending = pauseRequest.loading;

  // Bumped whenever a pause/unpause request resolves, so ListenForCallStatusContext can refetch
  // the live poll immediately instead of waiting up to 2s for its next scheduled tick.
  const [lastActionAt, setLastActionAt] = useState(0);
  useEffect(() => {
    if (pauseRequest.data) {
      setLastActionAt(Date.now());
    }
  }, [pauseRequest.data]);

  // Logon/logoff and pause/unpause are mutually exclusive actions - reset the other's cached
  // result so a stale response can't win the priority order above after the real latest action.
  const performUserLogOnOff = (args: any) => {
    resetPauseRequest();
    performUserLogOnOffRaw(args);
  };

  // pauseTypeId is optional - confirmed via native Puzzel Agent's own network traffic, which
  // sends an empty body ({}) for a generic Pause with no specific reason selected.
  const performAgentPauseOn = (pauseTypeId?: number) => {
    resetUserLogOnOffRequest();
    performAgentPauseRaw({
      param: { userId, pauseOnOrOff: "pauseon" },
      payload: pauseTypeId === undefined ? {} : { pauseTypeId },
    });
  };

  const performAgentPauseOff = () => {
    resetUserLogOnOffRequest();
    performAgentPauseRaw({
      param: { userId, pauseOnOrOff: "pauseoff" },
      payload: {},
    });
  };

  // Pause types are only meaningful once logged on, and don't change during a session.
  useEffect(() => {
    if (isAuthenticated && userId && isLoggedOn && !pauseTypesRequest.data) {
      performPauseTypesGet({ userId });
    }
  }, [isAuthenticated, userId, isLoggedOn]);

  // Shown when the account has no pause types configured in Puzzel - selecting it sends pauseon
  // with an empty body (no pauseTypeId), matching native Puzzel Agent's own generic Pause.
  const DEFAULT_PAUSE_TYPES: PauseType[] = [{ id: 0, description: "Pause" }];

  const fetchedPauseTypes: PauseType[] = pauseTypesRequest.data?.result ?? [];
  const hasRealPauseTypes = fetchedPauseTypes.length > 0;
  const pauseTypes: PauseType[] = hasRealPauseTypes
    ? fetchedPauseTypes
    : DEFAULT_PAUSE_TYPES;

  return {
    userDetails: {
      currentProfile,
      profiles,
      isLoggedOn,
      presenceStatus,
      isPresencePending,
      lastActionAt,
      pauseTypes,
      hasRealPauseTypes,
    },
    performUserLogOnOff,
    performAgentPauseOn,
    performAgentPauseOff,
  };
}

const LegacyBody: FC<{ children: ReactNode }> = ({ children }) => {
  const value = useLegacyCallStatus();
  return (
    <CallStatusContext.Provider value={value}>
      {children}
    </CallStatusContext.Provider>
  );
};

const OidcBody: FC<{ children: ReactNode }> = ({ children }) => {
  const value = useOidcCallStatus();
  return (
    <CallStatusContext.Provider value={value}>
      {children}
    </CallStatusContext.Provider>
  );
};

const UserDetailsAndLogOnOffProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { enabled: oidcEnabled } = useFeatureFlag(PUZZEL_OIDC_LOGIN_FLAG);
  return oidcEnabled ? (
    <OidcBody>{children}</OidcBody>
  ) : (
    <LegacyBody>{children}</LegacyBody>
  );
};

function useCallStatusContext(): CallStatusValue {
  const context = React.useContext(CallStatusContext);
  if (context === undefined) {
    throw new Error(
      "useUserDetailsState must be used within a UserDetailsAndLogOnOffProvider",
    );
  }
  return context;
}

function useUserDetailsState(): UserDetails {
  return useCallStatusContext().userDetails;
}

function usePerformUserLogOnOff(): (args: any) => void {
  return useCallStatusContext().performUserLogOnOff;
}

function usePerformAgentPauseOn(): (pauseTypeId?: number) => void {
  return useCallStatusContext().performAgentPauseOn;
}

function usePerformAgentPauseOff(): () => void {
  return useCallStatusContext().performAgentPauseOff;
}

export {
  usePerformAgentPauseOff,
  usePerformAgentPauseOn,
  usePerformUserLogOnOff,
  UserDetailsAndLogOnOffProvider,
  useUserDetailsState,
};
